import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../../assets/images";
import { useTranslate } from "../../../context/TranslationContext";
import {
  useBuyProductMutation,
  useGetPurchasedProductQuery,
} from "../../../redux/Apis/DigitalProducts";
import { useGetCurrentSubscriptionQuery } from "../../../redux/Apis/Packages";
import {
  convertPrice,
  formatCurrency,
  getConfigByCountryCode,
} from "../../../utils/localization";
import Button from "../../button";

const HeroSectionTemplateCard = ({
  file,
  title,
  subtitle,
  access_type,
  duration,
  pricing,
  _id,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslate("templatebanner");
  const lang = useSelector((state) => state.language.lang);
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const [displayPrice, setDisplayPrice] = useState("");
  const {
    data: current_subscription,
    isSuccess: currentSuccess,
    refetch,
  } = useGetCurrentSubscriptionQuery({lang}, { skip: !isLoggedIn });
  const [
    buyDigitalProduct,
    { data: buyData, isSuccess: buySuccess, error: buyError },
  ] = useBuyProductMutation();
  const { data: purchasedProductsRes, isSuccess: hasPurchasedData } =
    useGetPurchasedProductQuery({ params: { lang } }, { skip: !isLoggedIn });

  const purchasedProducts = purchasedProductsRes?.data || [];

  const isProductPurchased = purchasedProducts.some(
    (item) => item?.product?._id === _id
  );

  useEffect(() => {
    const updatePrice = async () => {
      if (!pricing) return;

      const selectedCountry =
        user?.country || localStorage?.getItem("selectedCountry") || "gb";
      const { currency } = getConfigByCountryCode(selectedCountry);

      const converted = await convertPrice(pricing, "GBP", currency);
      const formatted = formatCurrency(converted, selectedCountry);

      setDisplayPrice(formatted);
    };

    updatePrice();
  }, [pricing, user?.country]);

  useEffect(() => {
    if (buySuccess && buyData?.data?.checkoutUrl) {
      window.location.href = buyData.data.checkoutUrl;
    }
  }, [buySuccess, buyData]);

  useEffect(() => {
    if (isLoggedIn) {
      refetch();
    }
  }, [isLoggedIn, refetch]);

  useEffect(() => {
    if (buyError) {
      const errorMsg = buyError?.data?.message || t("toast.buyError");
      toast.error(errorMsg);
    }
  }, [buyError]);

  const handleClick = () => {
    navigate(`/templates/${_id}`);
  };

  const handleCTA = () => {
    if (!user?.role) {
      if (access_type === "free")
        return toast.info(t("toast.loginRequiredFree"));
      if (access_type === "membership")
        return toast.info(t("toast.loginRequiredMembership"));
      if (access_type === "paid")
        return toast.info(t("toast.loginRequiredPaid"));
      if (access_type === "custom")
        return toast.info(t("toast.loginRequiredCustom"));
      return;
    }

    if (user.role === "learner") {
      if (access_type === "free")
        return toast.info(t("toast.creatorRequiredFree"));
      if (access_type === "membership")
        return toast.info(t("toast.creatorRequiredMembership"));
      if (access_type === "paid")
        return toast.info(t("toast.creatorRequiredPaid"));
      if (access_type === "custom")
        return toast.info(t("toast.creatorRequiredCustom"));
      return;
    }

    const hasMembership =
      currentSuccess && current_subscription?.data?.status === "active";

    if (access_type === "free") {
      return navigate("/my-teaching", {
        state: {
          activeTab: "pills-pss",
          access_type,
          template_id: _id,
          title: title,
          template_price: pricing,
        },
      });
    }

    if (access_type === "membership") {
      if (hasMembership) {
        return navigate("/my-teaching", {
          state: {
            activeTab: "pills-pss",
            access_type,
            title: title,
            template_id: _id,
          },
        });
      }
      return toast.info(t("toast.membershipUpgradeNeeded"));
    }

    if (access_type === "paid") {
      if (isProductPurchased) {
        return navigate("/my-teaching", {
          state: {
            activeTab: "pills-pss",
            access_type,
            title: title,
            template_id: _id,
          },
        });
      }

      return buyDigitalProduct({
        id: _id,
        params: { lang },
      });
    }

    if (access_type === "custom") {
      return toast.success(t("toast.customSuccess"));
    }
  };

  const badgeText =
    access_type === "paid"
      ? isProductPurchased
        ? t("active")
        : t("buyFullBundle").replace("{{price}}", displayPrice || pricing)
      : access_type === "free"
      ? t("free")
      : t("membershipOnly");

  return (
    <div
      className="template-card d-flex flex-column"
      onClick={handleClick}
      style={{ cursor: "pointer" }}
    >
      <img
        src={file?.startsWith("http") ? file : images.home.template_default}
        alt={title}
        className="card-image"
      />

      <div
        className="card-body text-center mt-3 d-flex flex-column justify-content-between"
        style={{ flex: 1 }}
      >
        <div>
          <h5
            className="card-title mb-1 text-truncate-2"
            style={{ minHeight: "44px" }}
          >
            {title}
          </h5>

          <p
            className="card-subtitle mb-2"
            dangerouslySetInnerHTML={{
              __html: subtitle || "",
            }}
          />

          {duration && (
            <p
              className="duration-text mb-2"
              style={{ color: "#888", minHeight: "20px" }}
            >
              {duration}
            </p>
          )}
        </div>

        <Button
          text={badgeText}
          className="site-btn mt-2"
          onClick={(e) => {
            e.stopPropagation();
            handleCTA();
          }}
        />
      </div>
    </div>
  );
};

export default memo(HeroSectionTemplateCard);
